import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const PICKINGJOB_BASE = "/pickingjob";
const buildUrl = (endpoint) => PICKINGJOB_BASE + endpoint;

const pickingjobApiSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getOptionsWithCount: builder.query({
            query: (args) => ({
                url: buildUrl(`/options/count?type=${args.type}${args.urlquery ? args.urlquery : ""}`),
                method: "GET",
            }),
            providesTags: (result, error, arg) => {
                return [{ type: `Logs${arg.type}`, id: arg.type }];
            },
        }),
        getPickingJobs: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'PickingJobs' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getPickingJobs', 'PickingJobs'),
        }),
        getPickingJob: builder.query({
            query: id => ({
                url: buildUrl(`/${id}`),
                method: 'GET',
                meta: { customContentType: true }, 
            }),
            providesTags: () => [{ type: 'PickingJobsSingle' }],
        }),
        createPickingJob: builder.mutation({
            query: body => ({
                url: buildUrl('/new'),
                method:"POST",
                body
            }),
            invalidatesTags: (result, error, arg) => [{type: 'Orders' }, { type: 'OrdersSingle' }],
        }),
        getOptions: builder.query({
            query: ({type,body}) => ({
                url: buildUrl(`/options?type=${type}`),
                method: 'POST',
                body,
            }),
        }),
        getFilterCount: builder.query({
            query: (body) => ({
                url:buildUrl("/optioncount"),
                method:"POST",
                body
            })
        }),
        getPickingJobPickingList: builder.query({
            query: id => ({
                url: buildUrl(`/${id}/pickinglines`),
                method: 'GET',
                meta: { customContentType: true }, 
            }),
            providesTags: () => [{ type: 'PickingJobPickingLines' }],
        }),
        getPickingJobPickingListPDF: builder.mutation({
            query: id => ({
                url: buildUrl(`/${id}/pickinglines?type=pdf`),
                method: 'GET',
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const blob = new Blob([await response.blob()], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    return {data: url};
                },
            }),
        }),
        getPickingLines: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/pickinglines/query`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'PickingJobPickingLines' }],
        }),
        addNewPickingJob: builder.mutation({
            query: data => ({
                url: buildUrl(`/new`),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [`Orders`, 'PickingJobs'],
        }),
        updatePickingJobStepPickingLine: builder.mutation({
            query: pickingLineInfo => ({
                url: buildUrl(`/pickingjobstep/update/${pickingLineInfo.id}`),
                method: 'PUT',
                body: pickingLineInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'PickingJobPickingLines' }],
        }),
        finishPickingJob: builder.mutation({
            query: ({ pickingJobId, setPickedToOpen }) => ({
                url: buildUrl(`/${pickingJobId}/finish`),
                method: 'POST',
                body: { setPickedToOpen },
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ConceptPickingJobs' }],
        }),
        fullCompletePickingJob: builder.mutation({
            query: pickingJobId => ({
                url: buildUrl(`/${pickingJobId}/fullcomplete`),
                method: 'GET',
            }),
        }),
        openPickingJob: builder.mutation({
            query: pickingJobId => ({
                url: buildUrl(`/${pickingJobId}/toinprogress`),
                method: 'GET',
            }),
        }),
        unblockStep: builder.mutation({
            query: stepId => ({
                url: buildUrl(`/pickingjobstep/unblock/${stepId}`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: 'PickingJobPickingLines' }],
        }),
        findAllConceptJobs: builder.query({
            query: () => ({
                url: buildUrl("/concept"),
                method: "GET"
            }),
            providesTags: () => [{ type: 'ConceptPickingJobs' }],
        }),
        findConceptJob : builder.query({
            query: (jobId) => ({
                url: buildUrl(`/${jobId}/concept`),
                method: "GET"
            }),
            providesTags: (result, error, arg) => {
                return [{ type: `ConceptPickingJob`, id: arg }];
            },
        }),
        createConceptJob : builder.mutation({
            query: (data) => ({
                url: buildUrl(`/new/concept`),
                method: "POST",
                body:data,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ConceptPickingJobs' }],
        }),
        confirmConceptJob : builder.mutation({
            query: (data) => ({
                url: buildUrl(`/${data.jobId}/concept/confirm`),
                method: "POST",
                body:data,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ConceptPickingJobs' },{type: "AllPickingJobs"},{type:"PickingJobs"}, {type: 'ConceptPickingJob', id: arg.jobId }],
        }),
        getShippingProgress: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}/shipping-progress`),
                method: 'GET',
            }),
        }),
        getShippingProgressFromIndividualProduct: builder.query({
            query: ({id,workspace}) => ({
                url: buildUrl(`/${id}/shipping-progress?workspace=${workspace}`),
                method: 'GET',
            }),
        }),
        printBatchFromShippedOrderIds: builder.mutation({
            query: (body) => ({
                url: `/orders/${body.id}/batch-print`,
                method: 'POST',
                body,
            }),
        }),
    })
});

export const {
    usePrintBatchFromShippedOrderIdsMutation,
    useFindAllConceptJobsQuery,
    useFindConceptJobQuery,
    useConfirmConceptJobMutation,
    useCreateConceptJobMutation,
    useGetPickingJobsQuery,
    useGetPickingJobQuery,
    useGetPickingJobPickingListQuery,
    useGetPickingJobPickingListPDFMutation,
    useAddNewPickingJobMutation,
    useGetPickingLinesQuery,
    useUpdatePickingJobStepPickingLineMutation,
    useGetOptionsQuery,
    useGetFilterCountQuery,
    useCreatePickingJobMutation,
    useGetOptionsWithCountQuery,
    useFinishPickingJobMutation,
    useFullCompletePickingJobMutation,
    useOpenPickingJobMutation,
    useUnblockStepMutation,
    useGetShippingProgressQuery,
    useGetShippingProgressFromIndividualProductQuery
} = pickingjobApiSlice;
